import React, { useEffect, useRef, useState } from 'react';
import ChannelHelper from './ChannelHelper';
import { failed as notify } from '../common/Toastify';
import AudioEqualizer from './AudioEQ';
// import { error } from 'jquery'; // (optional) remove if unused
// import { loader } from '@monaco-editor/react'; // (optional) remove if unused

function notifyUser(message) {
  notify(message, 4000);
}

function Viewer({
  encounterId,
  clientId,
  useTrickleICE,
  openDataChannel,
  receiveAudio,
  recieveExamCameraVideo,
  recieveEarCameraVideo
}) {
  const signalingClientRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const [remoteAudioStream, setRemoteAudioStream] = useState(null);
  const [remoteExamVideoStream, setRemoteExamVideoStream] = useState(null);
  const [remoteEarVideoStream, setRemoteEarVideoStream] = useState(null);
  const remoteVideoRef = useRef(null);

  const expectingVideo = recieveExamCameraVideo || recieveEarCameraVideo;
  const hasVideo = Boolean(remoteExamVideoStream || remoteEarVideoStream);

  useEffect(() => {
    let cleanup = false;

    async function initViewer() {
      try {
        const channelHelper = new ChannelHelper(
          encounterId,
          {},
          null,
          'VIEWER',
          ChannelHelper.IngestionMode.OFF,
          '[VIEWER]',
          clientId,
        );
        await channelHelper.init();

        signalingClientRef.current = channelHelper.getSignalingClient();
        const pc = new RTCPeerConnection({ iceServers: channelHelper.iceServers || [] });
        peerConnectionRef.current = pc;

        const iceQueue = [];
        let signOpen = false;

        pc.onicecandidate = e => {
          if (e.candidate && useTrickleICE) {
            if (signOpen) signalingClientRef.current.sendIceCandidate(e.candidate);
            else iceQueue.push(e.candidate);
          }
        };

        pc.ontrack = e => {
          const [stream] = e.streams;

          // Audio path
          if (receiveAudio && stream.getAudioTracks().length > 0) {
            setRemoteAudioStream(stream);
          }

          // Video path
          if (expectingVideo && stream.getVideoTracks().length > 0) {
            if (remoteVideoRef.current) {
              remoteVideoRef.current.srcObject = stream;
              remoteVideoRef.current.muted = true;
            }
            if (recieveExamCameraVideo) setRemoteExamVideoStream(stream);
            if (recieveEarCameraVideo) setRemoteEarVideoStream(stream);
          }

          if (!receiveAudio && !expectingVideo) {
            notifyUser('No valid media stream received.');
          }
        };

        const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
        await pc.setLocalDescription(offer);

        signalingClientRef.current.on('sdpAnswer', async ans => {
          try {
            await pc.setRemoteDescription(ans);
          } catch (error) {
            console.error('[VIEWER] setRemoteDescription error', error);
            notifyUser('Connection error in Viewer. Please try again.');
          }
        });

        signalingClientRef.current.on('iceCandidate', c => pc.addIceCandidate(c));

        signalingClientRef.current.on('open', () => {
          signOpen = true;
          signalingClientRef.current.sendSdpOffer(pc.localDescription);
          iceQueue.forEach(c => signalingClientRef.current.sendIceCandidate(c));
        });

        signalingClientRef.current.open();
      } catch (e) {
        console.error('[VIEWER] init error', e);
      }
    }

    initViewer();

    return () => {
      cleanup = true;
      try { signalingClientRef.current?.close(); } catch {}
      try { peerConnectionRef.current?.close(); } catch {}
      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
      setRemoteExamVideoStream(null);
      setRemoteEarVideoStream(null);
      setRemoteAudioStream(null);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encounterId, clientId, useTrickleICE, receiveAudio, recieveExamCameraVideo, recieveEarCameraVideo]);

  return (
    <div>
      {receiveAudio && <AudioEqualizer stream={remoteAudioStream} />}

      {expectingVideo && (
        <div
          style={{
            position: 'relative',
            width: '100%',
            maxHeight: '80vh',
            background: '#000',
            borderRadius: 8,
            overflow: 'hidden',
            aspectRatio: '16 / 9'
          }}
        >
          {/* Waiting overlay */}
          {!hasVideo && (
            <div
              role="status"
              aria-live="polite"
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ddd',
                fontSize: 18,
                padding: 16,
                textAlign: 'center'
              }}
            >
              Waiting for kiosk…
            </div>
          )}

          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            style={{
              width: '100%',
              height: '100%',
              display: hasVideo ? 'block' : 'none',
              objectFit: 'contain'
            }}
          />
        </div>
      )}
    </div>
  );
}

export default Viewer;
